import { Star } from 'lucide-react';
import type { Player, PowerSnapshot } from '@/lib/api';
import { cn, formatPower } from '@/lib/utils';
import { MTypeIcon } from './MTypeIcon';

type Props = {
  player: Pick<Player, 'name' | 'neverSub'> & {
    displayName?: string | null;
    latestSnapshot?: PowerSnapshot | null;
  };
  snapshot?: PowerSnapshot | null;
  showStar?: boolean;
  showM1?: boolean;
  className?: string;
  nameClassName?: string;
};

/**
 * Inline label for a player. Shows Never-Sub star, name, and M1 type icon.
 * The name has a rich `title` tooltip with M1/M2/M3 power+type + kills.
 * The M1 icon has its own `title` showing just M1 power.
 */
export function PlayerLabel({
  player,
  snapshot: snapshotProp,
  showStar = true,
  showM1 = true,
  className,
  nameClassName,
}: Props) {
  const snapshot = snapshotProp ?? player.latestSnapshot ?? null;
  const label = player.displayName?.trim() || player.name;
  const showAlias = !!player.displayName?.trim() && player.displayName !== player.name;
  const tooltipParts: string[] = [];
  if (showAlias) tooltipParts.push(`In-game: ${player.name}`);
  if (snapshot) {
    tooltipParts.push(
      `Total ${formatPower(snapshot.totalPower)}`,
      `M1 ${formatPower(snapshot.m1Power)} ${snapshot.m1Type}`,
      `M2 ${formatPower(snapshot.m2Power)} ${snapshot.m2Type}`,
      `M3 ${formatPower(snapshot.m3Power)} ${snapshot.m3Type}`,
      `Kills ${formatPower(snapshot.kills)}`,
    );
  } else if (tooltipParts.length === 0) {
    tooltipParts.push('No snapshot yet');
  }
  const tooltip = tooltipParts.join(' · ');
  return (
    <span className={cn('inline-flex items-center gap-1', className)}>
      {showStar && player.neverSub && <Star size={11} className="text-amber-400" />}
      <span className={cn('truncate', nameClassName)} title={tooltip}>
        {label}
      </span>
      {showM1 && snapshot && (
        <span
          title={`M1 Power: ${formatPower(snapshot.m1Power)} ${snapshot.m1Type}`}
          className="shrink-0"
        >
          <MTypeIcon type={snapshot.m1Type} size={12} />
        </span>
      )}
    </span>
  );
}
